import { type IMediator } from '#/core/mediator';

import { RequestError } from '#/shared/exceptions/request-error';
import { SystemError } from '#/shared/exceptions/system-error';

import { SERVICES_TYPES } from '#/services-types';

import { type ISentryService } from '../sentry';
import { type IUrlParamsService, UrlParamsName } from '../url-params';

import { type ILogService } from './log.types';

export class LogService implements ILogService {
  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }
  get #sentryService() {
    return this._mediator.get<ISentryService>(SERVICES_TYPES.SentryService);
  }

  #log(
    systemLogger: Console['info'] | Console['warn'] | Console['error'],
    type: string,
    color: string,
    module: string,
    ...args: unknown[]
  ) {
    const styles = `font-weight: 700; font-size:12px; color:${color};`;

    systemLogger(`%c${type}:`, `${styles}`, `${module} ->`, ...args);
  }

  constructor(private readonly _mediator: IMediator) {}

  info(module: string, ...args: unknown[]) {
    if (this.#urlParamsService.hasParamByName(UrlParamsName.Debug)) {
      // eslint-disable-next-line no-console
      this.#log(console.info, 'INFO', 'royalBlue', module, ...args);
    }
  }

  warn(module: string, message: string, ...args: unknown[]) {
    // eslint-disable-next-line no-console
    this.#log(console.warn, 'WARN', 'darkOrange', module, message, ...args);

    this.#sentryService.captureWarning(`${module}: ${message}`, {
      tags: {
        module,
      },
      extra: {
        ...args,
      },
    });
  }

  error(module: string, error: SystemError | RequestError | Error) {
    const additionalInfo =
      error instanceof SystemError || error instanceof RequestError ? error.additionalInfo : undefined;

    // eslint-disable-next-line no-console
    this.#log(console.error, 'ERROR', 'fireBrick', module, error, additionalInfo);

    const { tags, ...extra } = additionalInfo || {};

    this.#sentryService.captureException(error, {
      tags: {
        module,
        ...tags,
      },
      extra,
    });
  }
}
